<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="relative inline-block">
    <button
      ref="buttonRef"
      @click.stop="toggle"
      class="p-0.5 hover:bg-[var(--color-primary-300)] rounded transition"
      :class="buttonClass"
      type="button">
      <Info :class="iconClass" class="text-[var(--color-text)] opacity-50" />
    </button>

    <!-- Backdrop to close tooltip -->
    <div v-if="isOpen" @click="close" class="fixed inset-0 z-[60]"></div>

    <transition name="fade">
      <div
        v-if="isOpen"
        ref="tooltipRef"
        @click.stop="close"
        :class="[
          'fixed z-[70] rounded-lg shadow-2xl leading-relaxed bg-[var(--color-primary-100)] text-[var(--color-text)] p-3 max-w-[90vw] cursor-pointer',
          warnings && warnings.length > 0
            ? 'border-2 border-orange-400'
            : 'border border-[var(--color-primary-500)]',
        ]"
        :style="tooltipStyle">
        <slot>
          <template v-if="warnings && warnings.length > 0">
            <div class="space-y-2">
              <div
                v-for="(warning, index) in warnings"
                :key="index"
                class="flex items-start gap-2">
                <component
                  :is="warning.passed ? CheckCircle : AlertTriangle"
                  class="w-4 h-4 flex-shrink-0 mt-0.5"
                  :class="
                    warning.passed ? 'text-green-500' : 'text-orange-500'
                  " />
                <span class="text-left leading-snug text-sm">{{
                  warning.message
                }}</span>
              </div>
            </div>
          </template>
          <template v-else>
            <span class="text-sm">{{ text }}</span>
          </template>
        </slot>
      </div>
    </transition>
  </div>
</template>

<script setup>
  import { ref, onMounted, onUnmounted, nextTick } from 'vue'
  import { Info, AlertTriangle, CheckCircle } from 'lucide-vue-next'

  const props = defineProps({
    text: {
      type: String,
      default: '',
    },
    warnings: {
      type: Array,
      default: null,
    },
    iconSize: {
      type: String,
      default: 'w-3.5 h-3.5',
    },
    buttonClass: {
      type: String,
      default: '',
    },
  })

  const isOpen = ref(false)
  const buttonRef = ref(null)
  const tooltipRef = ref(null)
  const tooltipStyle = ref({})

  const toggle = async () => {
    closeAllTooltips()
    isOpen.value = !isOpen.value

    if (isOpen.value) {
      // Set initial position immediately to avoid flicker
      setInitialPosition()
      await nextTick()
      // Refine position after render
      calculatePosition()
    }
  }

  const close = () => {
    isOpen.value = false
  }

  const closeAllTooltips = () => {
    document.querySelectorAll('.mini-tooltip-open').forEach((el) => {
      el.dispatchEvent(new CustomEvent('close-tooltip'))
    })
  }

  const setInitialPosition = () => {
    if (!buttonRef.value) return

    const buttonRect = buttonRef.value.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // Estimate tooltip size (will be refined after render)
    const estimatedWidth = 320
    const estimatedHeight = 200

    // Position below button by default
    let left = buttonRect.left
    let top = buttonRect.bottom + 8

    // If would overflow bottom, position above
    if (top + estimatedHeight > viewportHeight - 16) {
      top = buttonRect.top - estimatedHeight - 8
    }

    // If would overflow top, position below anyway
    if (top < 16) {
      top = buttonRect.bottom + 8
    }

    // Center horizontally relative to button
    left = buttonRect.left + buttonRect.width / 2 - estimatedWidth / 2

    // Adjust if would overflow horizontally
    if (left < 16) {
      left = 16
    }
    if (left + estimatedWidth > viewportWidth - 16) {
      left = viewportWidth - estimatedWidth - 16
    }

    tooltipStyle.value = {
      left: `${left}px`,
      top: `${top}px`,
    }
  }

  const calculatePosition = () => {
    if (!tooltipRef.value || !buttonRef.value) return

    const tooltip = tooltipRef.value
    const tooltipRect = tooltip.getBoundingClientRect()
    const buttonRect = buttonRef.value.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const padding = 16
    const offset = 8

    // Position below button by default
    let left = buttonRect.left + buttonRect.width / 2 - tooltipRect.width / 2
    let top = buttonRect.bottom + offset

    // If would overflow bottom, position above
    if (top + tooltipRect.height > viewportHeight - padding) {
      top = buttonRect.top - tooltipRect.height - offset
    }

    // If would overflow top, position below anyway
    if (top < padding) {
      top = buttonRect.bottom + offset
    }

    // Adjust horizontal position if would overflow
    if (left < padding) {
      left = padding
    }
    if (left + tooltipRect.width > viewportWidth - padding) {
      left = viewportWidth - tooltipRect.width - padding
    }

    tooltipStyle.value = {
      left: `${left}px`,
      top: `${top}px`,
    }
  }

  const iconClass = props.iconSize

  // Handle Escape key
  const handleEscape = (e) => {
    if (e.key === 'Escape' && isOpen.value) {
      close()
    }
  }

  onMounted(() => {
    document.addEventListener('keydown', handleEscape)
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', handleEscape)
  })
</script>
<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
